import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getCompanyReportsOtherActivitiesListQueryKey,
  getCompanyReportsRetrieveQueryKey,
  useCompanyReportsActivitiesDestroy,
  useCompanyReportsOtherActivitiesCreate,
  useCompanyReportsOtherActivitiesList,
  useCompanyReportsOtherActivitiesRetrieve,
  useCompanyReportsOtherActivitiesUpdate,
} from '@shared/api/lib/company-reports/company-reports';
import { CompanyReportsOtherActivitiesListParams } from '@shared/api/types/companyReportsOtherActivitiesListParams';
import { useQueryClient } from '@tanstack/react-query';

export const useDeleteOtherActivity = (activityTypeSlug: string) => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsOtherActivitiesListQueryKey(
            variables.companyReportUuid,
            activityTypeSlug
          )
        );
      },
    },
  });
};

export const useGetOtherActivityList = (
  companyReportUuid: string,
  activityTypeSlug: string,
  params?: CompanyReportsOtherActivitiesListParams
) => {
  return useCompanyReportsOtherActivitiesList(
    companyReportUuid,
    activityTypeSlug,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
        cacheTime: 1000 * 60 * 60 * 24,
      },
    }
  );
};

export const useGetOtherActivity = ({
  companyReportUuid,
  activityTypeSlug,
  activityUuid,
}: {
  companyReportUuid: string;
  activityTypeSlug: string;
  activityUuid?: string;
}) => {
  return useCompanyReportsOtherActivitiesRetrieve(
    companyReportUuid,
    activityTypeSlug,
    activityUuid!,
    {
      request: useAxiosConfig(),
      query: {
        enabled: !!activityUuid,
      },
    }
  );
};

export const useCreateOtherActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsOtherActivitiesCreate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsOtherActivitiesListQueryKey(
            variables.companyReportUuid,
            variables.activityTypeSlug
          )
        );
      },
    },
  });
};

export const useUpdateOtherActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsOtherActivitiesUpdate({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsOtherActivitiesListQueryKey(
            variables.companyReportUuid,
            variables.activityTypeSlug
          )
        );
      },
    },
  });
};
